/**
 * Parse custom UTM parameters from URL path
 * 
 * Format: /q/slug~pn~{partner}~cn~{campaign}~cid~{campaignId}~asid~{adsetId}~crid~{creativeId}
 * Maps to: pn→source, cn→campaign, cid→id, asid→term, crid→content
 * 
 * @returns Object with custom UTM values (only if they exist in path)
 */
const parseCustomPathParams = (): Partial<Record<'utm_source' | 'utm_campaign' | 'utm_id' | 'utm_term' | 'utm_content', string>> => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    // Extract slug portion from path (e.g., /q/shades~pn~google~cn~winter)
    const pathMatch = window.location.pathname.match(/^\/q\/([^/?]+)/);
    if (!pathMatch) return {};

    const fullSlug = pathMatch[1];
    const parts = fullSlug.split('~');

    // Parse tilde-separated key-value pairs
    const customParams: Record<string, string> = {};
    for (let i = 1; i < parts.length; i += 2) {
      const key = parts[i];
      const value = parts[i + 1];
      if (key && value) {
        customParams[key] = value;
      }
    }

    // Map custom params to UTM params
    return {
      utm_source: customParams['pn'] || undefined,     // Partner name → source
      utm_campaign: customParams['cn'] || undefined,   // Campaign name → campaign
      utm_id: customParams['cid'] || undefined,        // Campaign ID → id
      utm_term: customParams['asid'] || undefined,     // Ad set ID → term
      utm_content: customParams['crid'] || undefined,  // Creative ID → content
    };
  } catch {
    return {};
  }
};

export const buildQuickstartSteamUrl = (steamUrl: string): string => {
  if (typeof window === 'undefined') {
    return steamUrl;
  }

  // Ensure the URL has a protocol so it's treated as absolute, not relative
  let normalizedUrl = steamUrl;
  if (!/^https?:\/\//i.test(normalizedUrl)) {
    normalizedUrl = 'https://' + normalizedUrl;
  }

  try {
    const url = new URL(normalizedUrl);

    // Only apply UTM enrichment to Steam store URLs.
    // Non-Steam URLs (App Store, Singular, etc.) have their own tracking
    // parameters that should not be mutated.
    if (!url.hostname.includes('steampowered.com')) {
      return steamUrl;
    }

    const steamParams = url.searchParams;
    const pageParams = new URLSearchParams(window.location.search);

    // Parse custom path parameters as fallbacks
    const customParams = parseCustomPathParams();

    // Collect all sources that need to go into utm_medium
    const mediumParts: string[] = [];

    // 1. Get existing utm_source from Steam URL (if any)
    const steamUtmSource = steamParams.get('utm_source');
    if (steamUtmSource) {
      mediumParts.push(steamUtmSource);
    }

    // 2. Get utm_medium from page URL (if any)
    const pageUtmMedium = pageParams.get('utm_medium');
    if (pageUtmMedium) {
      mediumParts.push(pageUtmMedium);
    }

    // 3. Get utm_source from page URL query params OR custom path params (query takes priority)
    const pageUtmSource = pageParams.get('utm_source') || customParams.utm_source;
    if (pageUtmSource) {
      mediumParts.push(pageUtmSource);
    }

    // 4. Set utm_source to always be playcast_dropin
    steamParams.set('utm_source', 'playcast_dropin');

    // 5. Combine all sources into utm_medium
    if (mediumParts.length > 0) {
      steamParams.set('utm_medium', mediumParts.join('_'));
    }

    // 6. Set other UTM parameters (use query params first, fall back to custom path params)
    const utmParamsToSet = {
      utm_campaign: pageParams.get('utm_campaign') || customParams.utm_campaign,
      utm_id: pageParams.get('utm_id') || customParams.utm_id,
      utm_term: pageParams.get('utm_term') || customParams.utm_term,
      utm_content: pageParams.get('utm_content') || customParams.utm_content,
    };

    Object.entries(utmParamsToSet).forEach(([key, value]) => {
      if (value) {
        steamParams.set(key, value);
      }
    });

    // 7. Copy any remaining UTM parameters from page URL query (in case there are others)
    pageParams.forEach((value, key) => {
      if (!key.startsWith('utm_')) {
        return;
      }
      // Skip parameters we've already handled
      if (['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content'].includes(key)) {
        return;
      }
      if (value) {
        steamParams.set(key, value);
      }
    });

    url.search = steamParams.toString();
    return url.toString();
  } catch {
    return normalizedUrl;
  }
};

